import React, { useState } from 'react';
import { format } from 'date-fns';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { RefreshCcw, DollarSign, Calendar, User, FileText, CreditCard } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getFullName } from "@/utils/name-helpers";

interface BookingDetailModalProps {
  booking: any;
  isOpen: boolean;
  onClose: () => void;
  onRefund?: () => void;
}

export default function BookingDetailModal({ booking, isOpen, onClose, onRefund }: BookingDetailModalProps) {
  const [refundReason, setRefundReason] = useState('');
  const [isRefunding, setIsRefunding] = useState(false);
  const { toast } = useToast();

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'yyyy-MM-dd HH:mm');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const handleRefund = async () => {
    if (!refundReason.trim()) {
      toast({
        title: "Error",
        description: "Please provide a reason for the refund",
        variant: "destructive"
      });
      return;
    }

    setIsRefunding(true);
    try {
      const { data, error } = await supabase.functions.invoke('initiate-stripe-refund', {
        body: {
          booking_id: booking.id,
          reason: refundReason.trim(),
          refund_type: 'full'
        }
      });

      if (error) throw error;

      toast({
        title: "Refund Initiated",
        description: "The refund has been successfully initiated and will be processed shortly."
      });

      setRefundReason('');
      onRefund?.();
      onClose();
    } catch (error) {
      console.error('Refund error:', error);
      toast({
        title: "Refund Failed",
        description: "Failed to initiate refund. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsRefunding(false);
    }
  };

  if (!booking) return null;

  const clientName = getFullName(booking.client) || 'Unknown Client';
  const practitionerName = getFullName(booking.practitioner) || 'Unknown Practitioner';
  const serviceName = booking.service?.service_name || 'Unknown Service';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Booking Details - {booking.id.substring(0, 8)}...
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status and Payment Info */}
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">{booking.status}</Badge>
            <Badge variant="outline">{booking.payment_status}</Badge>
            {booking.refund_status && (
              <Badge variant="destructive">{booking.refund_status}</Badge>
            )}
          </div>

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Participants
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Client</label>
                <p className="font-medium">{clientName}</p>
                {booking.client?.email && (
                  <p className="text-sm text-muted-foreground">{booking.client.email}</p>
                )}
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-muted-foreground">Practitioner</label>
                <p className="font-medium">{practitionerName}</p>
                {booking.practitioner?.professional_title && (
                  <p className="text-sm text-muted-foreground">{booking.practitioner.professional_title}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Service & Schedule */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Service & Schedule
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Service</label>
                <p className="font-medium">{serviceName}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Format</label>
                <p className="capitalize">{booking.format}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Start Time</label>
                  <p>{formatDate(booking.start_time)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">End Time</label>
                  <p>{formatDate(booking.end_time)}</p>
                </div>
              </div>
              {booking.location_details && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Location Details</label>
                  <p className="text-sm">{booking.location_details}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Payment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Amount</label>
                  <p className="font-semibold text-lg">
                    {formatPrice(booking.price_at_booking, booking.currency_at_booking)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Payment Status</label>
                  <Badge variant="outline">{booking.payment_status}</Badge>
                </div>
              </div>
              {booking.stripe_session_id && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Stripe Session ID</label>
                  <p className="text-xs font-mono">{booking.stripe_session_id}</p>
                </div>
              )}
              {booking.refund_status && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Refund Status</label>
                  <p>{booking.refund_status}</p>
                  {booking.refund_reason && (
                    <p className="text-sm text-muted-foreground mt-1">Reason: {booking.refund_reason}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {(booking.client_notes || booking.practitioner_notes || booking.session_instructions) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {booking.client_notes && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Client Notes</label>
                    <p className="text-sm bg-muted p-2 rounded">{booking.client_notes}</p>
                  </div>
                )}
                {booking.practitioner_notes && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Practitioner Notes</label>
                    <p className="text-sm bg-muted p-2 rounded">{booking.practitioner_notes}</p>
                  </div>
                )}
                {booking.session_instructions && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Session Instructions</label>
                    <p className="text-sm bg-muted p-2 rounded">{booking.session_instructions}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Refund Action */}
          {booking.payment_status === 'Paid' && !booking.refund_status && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-destructive">
                  <DollarSign className="h-4 w-4" />
                  Refund Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="w-full">
                      <RefreshCcw className="h-4 w-4 mr-2" />
                      Initiate Full Refund
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Initiate Refund</AlertDialogTitle>
                      <AlertDialogDescription asChild>
                        <div className="space-y-4">
                          <p>
                            This will initiate a full refund of {formatPrice(booking.price_at_booking, booking.currency_at_booking)} 
                            to the customer's original payment method.
                          </p>
                          <div>
                            <label className="text-sm font-medium">Reason for refund (required)</label>
                            <Textarea
                              value={refundReason}
                              onChange={(e) => setRefundReason(e.target.value)}
                              placeholder="Please provide a reason for this refund..."
                              className="mt-1"
                              rows={3}
                            />
                          </div>
                        </div>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel onClick={() => setRefundReason('')}>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleRefund}
                        disabled={isRefunding || !refundReason.trim()}
                        className="bg-destructive hover:bg-destructive/90"
                      >
                        {isRefunding ? "Processing..." : "Confirm Refund"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          )}

          {/* Timestamps */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                <div>
                  <label>Created</label>
                  <p>{formatDate(booking.created_at)}</p>
                </div>
                {booking.updated_at && (
                  <div>
                    <label>Last Updated</label>
                    <p>{formatDate(booking.updated_at)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}